import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore
import '../App.css';

interface OrderStatus {
  status: string;
  amount?: number;
  platform?: string;
  created?: string;
  accountCheck?: string;
  accountCheckLong?: string;
  economyState?: string;
  economyStateLong?: string;
  amountOrdered?: number;
  percentDelivered?: number;
  coinsCustomerAccount?: number;
  wasAborted?: number;
  knownClub?: string;
  externalOrderID?: string;
}

interface CooldownData {
  success: boolean;
  cooldownRemaining: number;
  message: string;
  isReady: boolean;
  hours?: number;
  minutes?: number;
}

interface ErrorDetail {
  title: string;
  desc: string;
  steps: string[];
  autoFix?: boolean;
  isDailyLimit?: boolean;
  contactSupport?: boolean;
  canRetry?: boolean;
}

const ERROR_DETAILS: Record<string, ErrorDetail> = {
  wrongPersona: {
    title: 'Wrong Persona Selected',
    desc: 'The wrong FUT persona is selected on your account.',
    steps: [
      'Open the FUT web app at www.ea.com/fifa/ultimate-team/web-app',
      'Click your profile icon and switch to the correct FUT persona',
      'Log out completely from all devices and browsers',
    ],
    canRetry: true,
  },
  captcha: {
    title: 'CAPTCHA Verification Required',
    desc: 'EA is requesting a security check on your account.',
    steps: [
      'Open the FUT web app at www.ea.com/fifa/ultimate-team/web-app',
      'Log in and complete the CAPTCHA if prompted',
      'Log out completely',
    ],
    canRetry: true,
  },
  wrongUserPass: {
    title: 'Incorrect Login Credentials',
    desc: 'The email or password you provided is wrong or has been changed.',
    steps: [
      'Verify your EA account email and password are still correct',
      'Reset your password at account.ea.com if needed',
      'Contact the person who set up your order to update your credentials',
    ],
    contactSupport: true,
  },
  wrongBA: {
    title: 'Backup Code is Wrong',
    desc: 'Your backup code is incorrect or has already been used.',
    steps: [
      'Go to account.ea.com → Security → Login Verification → Backup Codes',
      'Generate a new set of backup codes',
      'Enter one of the new codes in the form below',
    ],
  },
  No2FA: {
    title: 'Login Verification (2FA) Not Set Up',
    desc: 'Your EA account has no Login Verification enabled. This is required for the transfer to work.',
    steps: [
      'Go to account.ea.com → Security → Login Verification',
      'Enable verification — choose Authenticator App or Email',
      'After enabling, go to Backup Codes and generate a new set',
      'Enter one of the new backup codes in the form below',
    ],
  },
  noTM: {
    title: 'No Transfer Market Access',
    desc: 'This EA account does not have Transfer Market access.',
    steps: [
      'Complete the FUT tutorial and play initial matches to unlock the Transfer Market',
      'Check your EA Play / EA Pro subscription if required',
      'Contact the person who set up your order if the issue persists',
    ],
    contactSupport: true,
  },
  tlFull: {
    title: 'Transfer List is Full',
    desc: 'Your FUT transfer list is at maximum capacity.',
    steps: [
      'Open the FUT web app or EA app',
      'Go to Transfers → Transfer List',
      'Sell or remove items to free up at least 5 slots',
      'Log out completely',
    ],
    canRetry: true,
  },
  receiverTLFull: {
    title: 'Your Transfer List is Full',
    desc: 'Your Transfer List has no space left — coins cannot be sent to your account until you free up some slots.',
    steps: [
      'Open the FUT web app at www.ea.com/fifa/ultimate-team/web-app',
      'Go to Transfers → Transfer List',
      'Sell or Quick Sell items to free up at least 5 slots',
      'Log out completely from all EA apps and browsers',
    ],
    canRetry: true,
  },
  FailedReceiverDeviceBan: {
    title: 'Your Account Has Been Flagged by EA',
    desc: 'EA has placed a device-level block on your account. This requires intervention.',
    steps: ['Do not log into your EA account — contact the person who set up your order immediately'],
    contactSupport: true,
  },
  FailWebAppNotYetUnlocked: {
    title: 'FUT Web App Not Yet Unlocked',
    desc: 'Your EA account has not yet unlocked the FUT Web App, which is required for transfers.',
    steps: [
      'Open FIFA/EA FC and complete the FUT tutorial',
      'Play at least a few matches to unlock the Web App',
      'Let the person who set up your order know when done',
    ],
    contactSupport: true,
  },
  FailWebAppCustomerLocked: {
    title: 'Your FUT Web App Session is Locked',
    desc: 'Your EA account web session is locked — usually because you are logged in on another device.',
    steps: [
      'Log out of the FUT web app at www.ea.com/fifa/ultimate-team/web-app',
      'Log out of the EA app on your phone and all other devices',
      'Wait 5 minutes, then try again',
    ],
    canRetry: true,
  },
  calcErrorMaintenance: { title: 'Temporary EA Maintenance', desc: 'EA is under maintenance — retrying automatically.', steps: [], autoFix: true },
  noPlayer:             { title: 'Locating Transfer (Retrying)', desc: 'No suitable player found — retrying automatically.', steps: [], autoFix: true },
  insufficientFunds:    { title: 'Supplier Restocking', desc: 'Supplier is being restocked — handled automatically.', steps: [], autoFix: true },
  belowMinTransfer: {
    title: 'Transfer Below Minimum Amount',
    desc: 'The remaining amount is below the minimum transfer threshold. Click Resume to continue with the remaining amount.',
    steps: ['Click Resume below to continue delivery'],
    canRetry: true,
  },
  notEnoughCoins: {
    title: 'Not Enough Coins in Account',
    desc: 'Your account needs a minimum of 1,500 coins to process transfers.',
    steps: [
      'Buy any cheap player from the Transfer Market to add coins to your account',
      'Once you have at least 1,500 coins, click Retry below',
    ],
    canRetry: true,
  },
  unassignedItemsPresent: {
    title: 'Unassigned Items Present',
    desc: 'Items sitting in your account need to be assigned before transfers can continue.',
    steps: [
      'Open the FUT web app or EA app',
      'Go to Club → Unassigned Items',
      'Move all items to your club or transfer list',
      'Log out completely',
    ],
    canRetry: true,
  },
  console: {
    title: 'Still Logged Into Ultimate Team on Console',
    desc: 'Your account is active in Ultimate Team on your PlayStation or Xbox.',
    steps: [
      'On your console, open FIFA/EA FC and go to Ultimate Team',
      'Press Options/Start → Quit to Main Menu (do not just close the game)',
      'Wait for the game to fully exit Ultimate Team',
    ],
    canRetry: true,
  },
  loginFailed: {
    title: 'Login Failed — Log Out Everywhere',
    desc: 'EA rejected the login — your account may be active on another device or browser.',
    steps: [
      'Log out of the EA app on all your phones and tablets',
      'Log out of the FUT web app at www.ea.com/fifa/ultimate-team/web-app',
      'Log out of the FIFA/FC companion app if you use it',
    ],
    canRetry: true,
  },
  LoginFailedDeviceBan: {
    title: 'Login Blocked by EA',
    desc: 'EA has blocked the login from our system. Our team needs to act on this.',
    steps: ['Do not log into your EA account yourself — contact the person who set up your order right away'],
    contactSupport: true,
  },
  FailedSessionExpiredCustomerLoggedIn: {
    title: 'You Are Logged Into EA App',
    desc: 'Your account is active in the EA app or web app on another device.',
    steps: [
      'Log out of the FUT web app at www.ea.com/fifa/ultimate-team/web-app',
      'Log out of the EA app on your phone',
      'Log out of the FIFA/FC companion app',
    ],
    canRetry: true,
  },
  'FailedSessionExpiredCustomerLoggedIn?': {
    title: 'You Are Logged Into EA App',
    desc: 'Your account is active in the EA app or web app on another device.',
    steps: [
      'Log out of the FUT web app at www.ea.com/fifa/ultimate-team/web-app',
      'Log out of the EA app on your phone',
    ],
    canRetry: true,
  },
  FailLoggedInConsoleTo: {
    title: 'You Are Logged Into EA App',
    desc: 'Your account is active on another device or browser.',
    steps: [
      'Log out of the FUT web app and EA app on all devices',
    ],
    canRetry: true,
  },
  FailedProxyConnectionError:  { title: 'Temporary Technical Issue',  desc: 'Retrying automatically. No action needed.', steps: [], autoFix: true },
  FailedProxyPoolExhausted:    { title: 'Temporary Network Issue',     desc: 'Retrying automatically. No action needed.', steps: [], autoFix: true },
  FailedCouldNotStartTo:       { title: 'Starting Transfer (Retrying)', desc: 'Retrying automatically.',                 steps: [], autoFix: true },
  noClub: {
    title: 'No FUT Club Found',
    desc: 'Your EA account has no active FUT club — coins cannot be received without one.',
    steps: [
      'Open FIFA/EA FC and start Ultimate Team',
      'Complete the initial FUT setup and play at least one match',
      'Contact the person who set up your order once your club is ready',
    ],
    contactSupport: true,
  },
  wrongConsole: {
    title: 'Platform Mismatch',
    desc: 'The platform on your order does not match your EA account.',
    steps: ['Contact the person who set up your order — they need to correct the platform'],
    contactSupport: true,
  },
  PlayerLostTempban:  { title: 'Daily Transfer Limit Reached', desc: '', steps: [], isDailyLimit: true, autoFix: true },
  customerTempban:    { title: 'Daily Transfer Limit Reached', desc: '', steps: [], isDailyLimit: true, autoFix: true },
  tempbanCooldown:    { title: 'Daily Transfer Limit Reached', desc: '', steps: [], isDailyLimit: true, autoFix: true },
  dailyReceiverLimit: { title: 'Daily Transfer Limit Reached', desc: '', steps: [], isDailyLimit: true, autoFix: true },
  senderTempban: {
    title: 'Supplier Temporarily Paused',
    desc: 'Our supplier has a brief EA cooldown — handled automatically. No action needed.',
    steps: [],
    autoFix: true,
  },
  noSuitableSender: {
    title: 'No Suitable Supplier',
    desc: 'No supplier is available for your order right now.',
    steps: ['Contact the person who set up your order — they will reassign it'],
    contactSupport: true,
  },
  transfersInProgress: {
    title: 'Transfer In Progress',
    desc: 'Transfers are being processed — updating shortly.',
    steps: [],
    autoFix: true,
  },
};

const DAILY_LIMIT_MSG =
  'The daily transfer limit has been reached for today. This is a normal safeguard — your order will automatically resume tomorrow. No action is needed on your end. You can log in, play normally, and even spend the coins if you want — the order will be resumed automatically in ~30 hours. You can check how long remaining till the daily limit resets using the button below. Thanks for your patience 👍';

const DAILY_LIMIT_KEYS = new Set(['PlayerLostTempban', 'customerTempban', 'tempbanCooldown', 'dailyReceiverLimit']);

const ERR_KEY_ALIASES: Record<string, string> = {
  'consoleLoggedIn': 'console', 'Console Logged In': 'console',
  'loginFailed': 'loginFailed', 'Login Failed': 'loginFailed',
  'wrongPersona': 'wrongPersona', 'Wrong Persona': 'wrongPersona',
  'captcha': 'captcha', 'Captcha': 'captcha',
  'wrongUserPass': 'wrongUserPass', 'Wrong User Pass': 'wrongUserPass',
  'wrongBA': 'wrongBA', 'Wrong BA': 'wrongBA',
  'No2FA': 'No2FA', 'No 2FA Set Up': 'No2FA', 'no2FA': 'No2FA', 'no2fa': 'No2FA',
  'FailNo2FA': 'No2FA', '2FADisabled': 'No2FA', 'Fail No 2FA': 'No2FA',
  'noTM': 'noTM', 'No TM': 'noTM',
  'tlFull': 'tlFull', 'TL Full': 'tlFull', 'Transfer List Full': 'tlFull',
  'receiverTLFull': 'receiverTLFull', 'FailedTLfullReceiver': 'receiverTLFull',
  'Receiver TL full': 'receiverTLFull', 'Receiver TL Full': 'receiverTLFull',
  'ReceiverTLFull': 'receiverTLFull', 'receiver TL full': 'receiverTLFull',
  'notEnoughCoins': 'notEnoughCoins', 'Not Enough Coins': 'notEnoughCoins',
  'unassignedItemsPresent': 'unassignedItemsPresent', 'Unassigned Items Present': 'unassignedItemsPresent',
  'LoginFailedDeviceBan': 'LoginFailedDeviceBan',
  'noClub': 'noClub', 'No Club': 'noClub',
  'wrongConsole': 'wrongConsole', 'Wrong Console': 'wrongConsole',
  'noSuitableSender': 'noSuitableSender', 'No Suitable Sender': 'noSuitableSender',
  'senderTempban': 'senderTempban', 'Sender Tempban': 'senderTempban',
  'transfersInProgress': 'transfersInProgress', 'Transfers In Progress': 'transfersInProgress',
  'PlayerLostTempban': 'PlayerLostTempban', 'Player Lost Tempban': 'PlayerLostTempban',
  'FailPlayerLostToTempban': 'PlayerLostTempban', 'FailListTempBanCustomer': 'PlayerLostTempban',
  'customerTempban': 'customerTempban', 'Customer Tempban': 'customerTempban',
  'tempbanCooldown': 'tempbanCooldown', 'Tempban Cooldown': 'tempbanCooldown',
  'dailyReceiverLimit': 'dailyReceiverLimit',
  'FailedProxyConnectionError': 'FailedProxyConnectionError',
  'FailedProxyPoolExhausted': 'FailedProxyPoolExhausted',
  'FailedCouldNotStartTo': 'FailedCouldNotStartTo',
  'FailedSessionExpiredCustomerLoggedIn': 'FailedSessionExpiredCustomerLoggedIn',
  'FailedSessionExpiredCustomerLoggedIn?': 'FailedSessionExpiredCustomerLoggedIn?',
  'FailLoggedInConsoleTo': 'FailLoggedInConsoleTo',
  'FailedWrongCredentialsTo':         'wrongUserPass',
  'FailedWrongBACodeTo':               'wrongBA',
  'FailNoClubToCanBeEAErrorTryAgain':  'noClub',
  'FailedReceiverDeviceBan':           'FailedReceiverDeviceBan',
  'FailWebAppNotYetUnlocked':          'FailWebAppNotYetUnlocked',
  'FailWebAppCustomerLocked':          'FailWebAppCustomerLocked',
  'calcErrorMaintenance':              'calcErrorMaintenance',
  'noPlayer':                          'noPlayer',
  'insufficientFunds':                 'insufficientFunds',
  'belowMinTransfer':                  'belowMinTransfer',
  'transferCycleComplete':             'transfersInProgress',
  'customerHasPlayer':                 'transfersInProgress',
  'customerListedPlayer':              'transfersInProgress',
};

const resolveErrKey = (val?: string): string | null => {
  if (!val) return null;
  if (ERROR_DETAILS[val]) return val;
  return ERR_KEY_ALIASES[val] ?? null;
};

const platformLabel = (p?: string) => ({ PS: 'PlayStation', XB: 'Xbox', PC: 'PC' }[p ?? ''] || p || '');

const fmtTime = (ts: string) => {
  try {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? ts : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
};

interface Props { customerName: string; }

const TrackOrderPage: React.FC<Props> = ({ customerName }) => {
  const [data, setData]               = useState<OrderStatus | null>(null);
  const [loading, setLoading]         = useState(true);
  const [fetchError, setFetchError]   = useState<string | null>(null);
  const [refreshing, setRefreshing]   = useState(false);

  const [ssVer, setSsVer]   = useState(Date.now());
  const [ss1Ok, setSs1Ok]   = useState(false);
  const [ss2Ok, setSs2Ok]   = useState(false);
  const prevStatusRef = useRef<string | null>(null);

  const [user, setUser]             = useState('');
  const [pass, setPass]             = useState('');
  const [ba, setBa]                 = useState('');
  const [ba2, setBa2]               = useState('');
  const [ba3, setBa3]               = useState('');
  const [showExtra, setShowExtra]   = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError]     = useState<string | null>(null);

  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeMsg, setResumeMsg] = useState<{ type: 'success' | 'warning' | 'danger'; text: string } | null>(null);

  const [cooldown, setCooldown]               = useState<CooldownData | null>(null);
  const [cooldownLoading, setCooldownLoading] = useState(false);

  const [baNew, setBaNew]           = useState('');
  const [baLoading, setBaLoading]   = useState(false);
  const [baResult, setBaResult]     = useState<'success' | 'error' | null>(null);
  const [newUser, setNewUser]       = useState('');
  const [newPass, setNewPass]       = useState('');
  const [passLoading, setPassLoading] = useState(false);
  const [passResult, setPassResult]   = useState<'success' | 'error' | null>(null);

  const apiBase = `/api/orders/${encodeURIComponent(customerName)}`;

  const fetchStatus = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await fetch(apiBase);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setFetchError(d.error || 'Order not found');
        return;
      }
      const d: OrderStatus = await res.json();
      setData(d);
      setFetchError(null);
      if (isManual || d.status !== prevStatusRef.current) {
        setSsVer(Date.now());
        setSs1Ok(false);
        setSs2Ok(false);
        prevStatusRef.current = d.status;
      }
    } catch {
      setFetchError('Network error — retrying…');
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(() => fetchStatus(), 12000);
    return () => clearInterval(iv);
  }, [customerName]); // eslint-disable-line

  const handleSubmitCredentials = async () => {
    if (!user.trim() || !pass.trim() || !ba.trim()) return;
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const body: Record<string, string> = { user: user.trim(), pass: pass.trim(), ba: ba.trim() };
      if (ba2.trim()) body.ba2 = ba2.trim();
      if (ba3.trim()) body.ba3 = ba3.trim();
      const res = await fetch(`${apiBase}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) setSubmitError(d.error || 'Failed to submit. Please try again.');
      else await fetchStatus(true);
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleResume = async () => {
    if (resumeLoading) return;
    setResumeLoading(true);
    setResumeMsg(null);
    try {
      const res = await fetch(`${apiBase}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await res.json().catch(() => ({}));
      // resumeOrderAPI returns 200 {outcome:"resumed"} on success.
      if (res.ok) {
        setResumeMsg({ type: 'success', text: 'Order resumed! Refreshing status…' });
        setTimeout(() => fetchStatus(), 2500);
      } else if (res.status === 429) {
        // 429 = short cooldown after a console login failure. Just wait and retry.
        setResumeMsg({ type: 'warning', text: 'Please wait a moment after logging out, then try again.' });
      } else if (res.status === 405) {
        // 405 = the order has a temp ban (NOT a daily limit). It clears on its own
        // and the order auto-resumes; surface the real reason if the API gave one.
        const reason = d?.error || d?.reason || d?.raw;
        setResumeMsg({
          type: 'warning',
          text: reason
            ? `Can't resume yet — ${reason}. It will resume automatically.`
            : "Can't resume just yet — your order will resume automatically. Try again shortly.",
        });
        setTimeout(() => fetchStatus(), 2500);
      } else {
        setResumeMsg({ type: 'danger', text: d?.error || 'Resume failed. Please contact support.' });
      }
    } catch {
      setResumeMsg({ type: 'danger', text: 'Network error. Please try again.' });
    } finally {
      setResumeLoading(false);
    }
  };

  // Retry for onboarding/login-stuck orders (console / EA-app logged in).
  // Hits correctCredentialsAPI with continue:1 (re-runs the process) instead of
  // resumeOrderAPI, which returns a temp-ban 405 for these states.
  const handleRetry = async () => {
    if (resumeLoading) return;
    setResumeLoading(true);
    setResumeMsg(null);
    try {
      const res = await fetch(`${apiBase}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setResumeMsg({ type: 'success', text: 'Retrying now! Refreshing status…' });
        setTimeout(() => fetchStatus(), 2500);
      } else if (res.status === 429) {
        // Short cooldown after a console login failure — wait then retry.
        setResumeMsg({ type: 'warning', text: 'Please wait a moment after logging out, then try again.' });
      } else {
        setResumeMsg({ type: 'danger', text: d?.error || 'Retry failed — please try again shortly.' });
        setTimeout(() => fetchStatus(), 2500);
      }
    } catch {
      setResumeMsg({ type: 'danger', text: 'Network error. Please try again.' });
    } finally {
      setResumeLoading(false);
    }
  };

  const handleCooldown = async () => {
    if (cooldownLoading) return;
    setCooldownLoading(true);
    try {
      const res = await fetch(`${apiBase}/cooldown`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (res.ok) setCooldown(await res.json());
    } catch { /* ignore */ } finally {
      setCooldownLoading(false);
    }
  };

  const handleCorrectPass = async () => {
    if ((!newUser.trim() && !newPass.trim()) || passLoading) return;
    setPassLoading(true);
    setPassResult(null);
    try {
      const res = await fetch(`${apiBase}/correctBA`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: newUser.trim() || undefined, pass: newPass.trim() || undefined }),
      });
      setPassResult(res.ok ? 'success' : 'error');
      if (res.ok) { setNewUser(''); setNewPass(''); setTimeout(fetchStatus, 3000); }
    } catch { setPassResult('error'); }
    finally { setPassLoading(false); }
  };

  const handleCorrectBA = async () => {
    if (!baNew.trim() || baLoading) return;
    setBaLoading(true);
    setBaResult(null);
    try {
      const res = await fetch(`${apiBase}/correctBA`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ba: baNew.trim() }),
      });
      if (res.ok) { setBaResult('success'); setBaNew(''); setTimeout(() => fetchStatus(), 3000); }
      else setBaResult('error');
    } catch {
      setBaResult('error');
    } finally {
      setBaLoading(false);
    }
  };

  // ── Derived state ─────────────────────────────────────────────
  const isPending     = data?.status === 'pending';
  const isCancelled   = data?.status === 'cancelled';
  const isFinished    = data?.status === 'finished';
  const isInterrupted = data?.status === 'interrupted';
  const isTempbanStatus = data?.status === 'tempbanCooldown';

  const delivered = data?.amount ?? 0;
  const ordered   = data?.amountOrdered ?? data?.amount ?? 0;
  const remaining = ordered > 0 ? Math.max(ordered - delivered, 0) : 0;
  const pct       = data?.percentDelivered ?? 0;
  const showProgress = !isPending && data && ordered > 0;

  const activeErrKeys = new Set<string>();
  const acKey = resolveErrKey(data?.accountCheck) ?? resolveErrKey(data?.accountCheckLong);
  if (acKey) activeErrKeys.add(acKey);
  const esKey = resolveErrKey(data?.economyState) ?? resolveErrKey(data?.economyStateLong);
  if (esKey) activeErrKeys.add(esKey);
  if (isTempbanStatus) activeErrKeys.add('tempbanCooldown');

  const isDailyLimit = Array.from(activeErrKeys).some(k => DAILY_LIMIT_KEYS.has(k));
  // These errors can arrive via accountCheck OR economyState (e.g. FailNo2FA)
  const isWrongBA       = acKey === 'wrongBA'       || esKey === 'wrongBA';
  const isNo2FA         = acKey === 'No2FA'         || esKey === 'No2FA';
  const isWrongUserPass = acKey === 'wrongUserPass' || esKey === 'wrongUserPass';

  const nonDailyErrors   = Array.from(activeErrKeys).filter(k => !DAILY_LIMIT_KEYS.has(k));
  const actionableErrors = nonDailyErrors.filter(k => !ERROR_DETAILS[k]?.autoFix && k !== 'wrongBA' && k !== 'No2FA' && k !== 'wrongUserPass');
  const autoFixErrors    = nonDailyErrors.filter(k => ERROR_DETAILS[k]?.autoFix);

  // Hero
  const getHero = () => {
    if (loading)    return { color: 'muted',   icon: 'fa-circle-notch fa-spin', label: 'Loading…',              badge: '' };
    if (fetchError) return { color: 'danger',  icon: 'fa-exclamation-circle',   label: 'Order Not Found',        badge: '' };
    if (!data)      return { color: 'muted',   icon: 'fa-question-circle',       label: 'Unknown',               badge: '' };
    if (isCancelled) return { color: 'danger', icon: 'fa-ban',                  label: 'Order Cancelled',        badge: 'CANCELLED' };
    if (isPending)   return { color: 'info',   icon: 'fa-clock',                label: 'Awaiting Your Details',  badge: 'ACTION REQUIRED' };
    if (isFinished)  return { color: 'success',icon: 'fa-check-circle',         label: 'Delivery Complete!',     badge: 'DONE' };
    if (isDailyLimit) return { color: 'warning', icon: 'fa-moon',               label: 'Daily Limit Reached',   badge: 'AUTO-RESUMES TOMORROW' };
    if (isNo2FA)         return { color: 'danger', icon: 'fa-shield-alt', label: 'Enable Login Verification',   badge: 'ACTION REQUIRED' };
    if (isWrongBA)       return { color: 'danger', icon: 'fa-key',        label: 'New Backup Code Needed',       badge: 'ACTION REQUIRED' };
    if (isWrongUserPass) return { color: 'danger', icon: 'fa-lock',       label: 'Incorrect Login Credentials',  badge: 'ACTION REQUIRED' };
    if (actionableErrors.length > 0) {
      const first = ERROR_DETAILS[actionableErrors[0]];
      if (first?.contactSupport) return { color: 'danger',  icon: 'fa-life-ring',          label: first.title, badge: 'CONTACT SUPPORT' };
      return                              { color: 'warning', icon: 'fa-exclamation-triangle', label: first?.title || 'Action Required', badge: 'ACTION REQUIRED' };
    }
    if (autoFixErrors.length > 0 && !isInterrupted)
      return { color: 'active', icon: 'fa-circle-notch fa-spin', label: 'Processing', badge: 'AUTO-RETRY' };
    if (isInterrupted) return { color: 'warning', icon: 'fa-pause-circle', label: 'Order Paused', badge: 'ACTION REQUIRED' };
    return { color: 'active', icon: 'fa-circle-notch fa-spin', label: 'Delivering Your Coins', badge: 'IN PROGRESS' };
  };

  const hero = getHero();
  const displayStatus = data
    ? data.status.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()
    : null;

  const alertIcon = (type: string) =>
    type === 'success' ? 'fa-check-circle' : type === 'warning' ? 'fa-clock' : 'fa-exclamation-circle';

  return (
    <div className="ot-page">
      {/* ── Nav ──────────────────────────────────────────── */}
      <nav className="ot-nav">
        <div className="ot-nav-brand">
          <img
            src="https://i.ibb.co/BV9cX3cX/Whats-App-Image-2026-05-31-at-11-43-18-PM.jpg"
            alt="On Time Store"
            className="ot-nav-logo"
          />
          <span className="ot-nav-name"><span>On Time</span> Store</span>
        </div>
        <span className="ot-nav-badge">Order Tracker</span>
      </nav>

      {/* ── Hero ─────────────────────────────────────────── */}
      <div className={`ot-hero ot-hero-${hero.color}`}>
        <div className="ot-hero-inner">
          <div className="ot-hero-icon">
            <i className={`fas ${hero.icon}`}></i>
          </div>
          <div className="ot-hero-body">
            {hero.badge && <div className="ot-hero-badge">{hero.badge}</div>}
            <h1 className="ot-hero-title">{hero.label}</h1>
            <div className="ot-hero-sub">
              <span>Order #{customerName}</span>
              {data?.platform && <span className="ot-platform">{platformLabel(data.platform)}</span>}
            </div>
            {showProgress && pct > 0 && (
              <div className="ot-hero-pct">
                {pct}% — {delivered.toLocaleString()}K of {ordered.toLocaleString()}K coins
              </div>
            )}
          </div>
          <button
            className="ot-hero-refresh"
            onClick={() => fetchStatus(true)}
            disabled={refreshing || loading}
            title="Refresh"
          >
            <i className={`fas fa-sync-alt${refreshing ? ' fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* ── Content ──────────────────────────────────────── */}
      <div className="ot-content">
        {loading && (
          <div className="ot-alert ot-alert-info">
            <i className="fas fa-circle-notch fa-spin"></i>
            Loading your order…
          </div>
        )}
        {fetchError && (
          <div className="ot-alert ot-alert-danger">
            <i className="fas fa-exclamation-circle"></i>
            {fetchError}
          </div>
        )}

        {/* CANCELLED */}
        {!loading && !fetchError && isCancelled && (
          <div className="ot-alert ot-alert-danger" style={{ alignItems: 'flex-start' }}>
            <i className="fas fa-ban" style={{ marginTop: 2 }}></i>
            <div>
              <strong>This order has been cancelled.</strong><br />
              <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
                Contact the person who set up your order if you think this is a mistake.
              </span>
            </div>
          </div>
        )}

        {/* PENDING */}
        {!loading && !fetchError && isPending && !isCancelled && (
          <>
            <div className="ot-banner">
              <i className="fas fa-bolt ot-banner-icon"></i>
              <div>
                Your order for <strong>{data!.amount?.toLocaleString()}K coins</strong> on{' '}
                <strong>{platformLabel(data!.platform)}</strong> is ready.
                Enter your EA account details below to start the delivery.
              </div>
            </div>

            <div className="ot-card">
              <div className="ot-card-header">
                <div className="ot-card-header-icon"><i className="fas fa-gamepad"></i></div>
                <div>
                  <h2>EA Account Details</h2>
                  <p>Your credentials are sent securely and never stored</p>
                </div>
              </div>
              <div className="ot-card-body">
                <div className="ot-alert ot-alert-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>
                    <strong>Before submitting:</strong> Log out of the EA app, FUT web app, and companion app on all devices.
                  </span>
                </div>
                {submitError && (
                  <div className="ot-alert ot-alert-danger">
                    <i className="fas fa-exclamation-circle"></i>
                    {submitError}
                  </div>
                )}
                <div className="ot-form">
                  <div className="ot-field">
                    <label className="ot-label">EA Account Email</label>
                    <input className="ot-input" type="email" placeholder="your@email.com"
                      value={user} onChange={e => setUser(e.target.value)} disabled={submitLoading} />
                  </div>
                  <div className="ot-field">
                    <label className="ot-label">EA Account Password</label>
                    <input className="ot-input" type="password" placeholder="Your EA password"
                      value={pass} onChange={e => setPass(e.target.value)} disabled={submitLoading} />
                  </div>
                  <div className="ot-field">
                    <label className="ot-label">
                      Backup Code #1
                      <span className="ot-label-hint">— from EA Security Settings</span>
                    </label>
                    <input className="ot-input" type="text" placeholder="e.g. 12345678"
                      value={ba} onChange={e => setBa(e.target.value)} disabled={submitLoading} />
                  </div>
                  {showExtra && (
                    <>
                      <div className="ot-field">
                        <label className="ot-label">Backup Code #2<span className="ot-label-hint"> — optional</span></label>
                        <input className="ot-input" type="text" placeholder="e.g. 87654321"
                          value={ba2} onChange={e => setBa2(e.target.value)} disabled={submitLoading} />
                      </div>
                      <div className="ot-field">
                        <label className="ot-label">Backup Code #3<span className="ot-label-hint"> — optional</span></label>
                        <input className="ot-input" type="text" placeholder="e.g. 11223344"
                          value={ba3} onChange={e => setBa3(e.target.value)} disabled={submitLoading} />
                      </div>
                    </>
                  )}
                  <button type="button" className="ot-link-btn" onClick={() => setShowExtra(v => !v)}>
                    {showExtra ? '− Hide extra codes' : '+ Add more backup codes (recommended)'}
                  </button>
                  <button
                    className="ot-btn ot-btn-primary ot-btn-block"
                    onClick={handleSubmitCredentials}
                    disabled={submitLoading || !user.trim() || !pass.trim() || !ba.trim()}
                  >
                    <i className={`fas ${submitLoading ? 'fa-circle-notch fa-spin' : 'fa-bolt'}`}></i>
                    {submitLoading ? 'Starting delivery…' : 'Start My Delivery'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ACTIVE */}
        {!loading && !fetchError && !isPending && !isCancelled && data && (
          <>
            {isFinished && (
  <div className="ot-alert ot-alert-success">
    <i className="fas fa-check-circle"></i>
    <div>
      <strong>Your coins have been delivered! ⚽</strong><br />
      <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
        Thank you for choosing On Time Store. Enjoy!
      </span>
      <br />
      <br />
      <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
        The order is finished. Please keep in mind that there is a cooldown of 30 minutes before you can use the web app again. Alternatively, you can log in on console and log out again. Thank you for using our system.
      </span>
    </div>
  </div>
)}

            {!isFinished && (
              <div className="ot-banner">
                <i className="fas fa-shield-alt ot-banner-icon"></i>
                <div>
                  <strong>Stay logged out</strong> of the EA app, FUT web app, and companion app on all devices while your order is active.
                  <span className="ot-banner-meta"> · ~10 min per 100K · Daily limit auto-resumes next day</span>
                </div>
              </div>
            )}

            {/* Daily Limit */}
            {isDailyLimit && (
              <div className="ot-daily-limit">
                <div className="ot-daily-header">
                  <i className="fas fa-moon"></i>
                  Daily Transfer Limit Reached
                </div>
                <p className="ot-daily-body">{DAILY_LIMIT_MSG}</p>
              </div>
            )}

            {/* Actionable Errors */}
            {actionableErrors.map(key => {
              const err = ERROR_DETAILS[key];
              if (!err) return null;
              return (
                <div key={key} className={`ot-error-block${err.contactSupport ? ' ot-error-support' : ''}`}>
                  <div className="ot-error-header">
                    <i className={`fas ${err.contactSupport ? 'fa-life-ring' : 'fa-exclamation-triangle'}`}></i>
                    {err.title}
                  </div>
                  {err.desc && <p className="ot-error-desc">{err.desc}</p>}
                  {err.steps.length > 0 && (
                    <ol className="ot-error-steps">
                      {err.steps.map((s, i) => <li key={i} className="ot-error-step">{s}</li>)}
                    </ol>
                  )}
                  {err.canRetry && (
                    <>
                      <button
                        className="ot-btn ot-btn-primary"
                        onClick={handleRetry}
                        disabled={resumeLoading}
                        style={{ marginTop: 4 }}
                      >
                        <i className={`fas ${resumeLoading ? 'fa-circle-notch fa-spin' : 'fa-redo'}`}></i>
                        {resumeLoading ? 'Retrying…' : "I've fixed it — Retry"}
                      </button>
                      {resumeMsg && (
                        <div className={`ot-alert ot-alert-${resumeMsg.type}`} style={{ marginTop: 4, marginBottom: 0 }}>
                          <i className={`fas ${alertIcon(resumeMsg.type)}`}></i>
                          {resumeMsg.text}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {/* Auto-fix notice */}
            {autoFixErrors.length > 0 && actionableErrors.length === 0 && !isDailyLimit && (
              <div className="ot-alert ot-alert-info">
                <i className="fas fa-circle-notch fa-spin"></i>
                <div>
                  <strong>{ERROR_DETAILS[autoFixErrors[0]]?.title}</strong><br />
                  <span style={{ color: 'var(--text-2)' }}>{ERROR_DETAILS[autoFixErrors[0]]?.desc}</span>
                </div>
              </div>
            )}

            {/* Metrics */}
            {showProgress && (
              <div className="ot-metrics">
                <div className="ot-metric">
                  <span className="ot-metric-label"><i className="fas fa-shopping-cart"></i> Ordered</span>
                  <span className="ot-metric-value">{ordered.toLocaleString()}K</span>
                </div>
                <div className="ot-metric ot-metric-delivered">
                  <span className="ot-metric-label"><i className="fas fa-check"></i> Delivered</span>
                  <span className="ot-metric-value">{delivered.toLocaleString()}K</span>
                </div>
                <div className="ot-metric">
                  <span className="ot-metric-label"><i className="fas fa-hourglass-half"></i> Remaining</span>
                  <span className="ot-metric-value">{remaining.toLocaleString()}K</span>
                </div>
              </div>
            )}

            {/* Progress Bar */}
            {showProgress && (
              <div className="ot-progress-block">
                <div className="ot-progress-header">
                  <span>Delivery Progress</span>
                  <span>{pct}%</span>
                </div>
                <div className="ot-progress-track" style={{ position: 'relative', overflow: 'visible' }}>
                  <div className="ot-progress-fill" style={{ width: `${pct}%` }} />
                  {/* Running player */}
                  {pct > 0 && pct < 100 && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: `${pct}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      filter: 'drop-shadow(0 0 6px #d40000)',
                    }}>
                      <i
                        className="fas fa-running"
                        style={{
                          fontSize: '1.4rem',
                          color: '#ffffff',
                          animation: 'ot-runner-bounce 0.5s ease-in-out infinite alternate',
                          display: 'block',
                        }}
                      />
                    </div>
                  )}
                  {pct === 100 && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      right: -4,
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                    }}>
                      <i className="fas fa-flag-checkered" style={{ fontSize: '1.2rem', color: '#fff' }} />
                    </div>
                  )}
                </div>
                <div className="ot-progress-footer">
                  <span>{pct === 0 ? 'Starting…' : pct < 100 ? 'In progress' : 'Complete ⚽'}</span>
                  <span>{delivered.toLocaleString()}K / {ordered.toLocaleString()}K</span>
                </div>
              </div>
            )}

            {/* Wrong BA / No2FA Form */}
            {(isWrongBA || isNo2FA) && (
              <div className="ot-action-block">
                <div className="ot-action-header">
                  <div className="ot-action-header-left">
                    <i className={`fas ${isNo2FA ? 'fa-shield-alt' : 'fa-key'}`}></i>
                    {isNo2FA ? 'Enable Login Verification & Enter Backup Code' : 'New Backup Code Required'}
                  </div>
                </div>
                <p className="ot-action-desc">
                  {isNo2FA
                    ? 'Your EA account has no Login Verification set up. Enable it first, then generate backup codes and enter one below:'
                    : 'Your backup code is incorrect or expired. Follow these steps then enter a new code below:'}
                </p>
                <ol className="ot-error-steps">
                  {isNo2FA ? (
                    <>
                      <li className="ot-error-step">Go to <strong>account.ea.com</strong> → Security → Login Verification</li>
                      <li className="ot-error-step">Enable verification — choose Authenticator App or Email</li>
                      <li className="ot-error-step">After enabling, go to <strong>Backup Codes</strong> and generate a new set</li>
                      <li className="ot-error-step">Enter one of the new codes below</li>
                    </>
                  ) : (
                    <>
                      <li className="ot-error-step">Go to <strong>account.ea.com</strong> → Security → Login Verification → Backup Codes</li>
                      <li className="ot-error-step">Generate a new set of backup codes</li>
                      <li className="ot-error-step">Enter one of the new codes below</li>
                    </>
                  )}
                </ol>
                <div className="ot-ba-row">
                  <input className="ot-ba-input" type="text" placeholder="New backup code"
                    value={baNew} onChange={e => setBaNew(e.target.value)} disabled={baLoading} maxLength={20} />
                  <button className="ot-btn ot-btn-primary" onClick={handleCorrectBA} disabled={baLoading || !baNew.trim()}>
                    <i className={`fas ${baLoading ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'}`}></i>
                    Submit
                  </button>
                </div>
                {baResult === 'success' && (
                  <div className="ot-alert ot-alert-success">
                    <i className="fas fa-check-circle"></i>
                    Backup code updated! Retrying your order…
                  </div>
                )}
                {baResult === 'error' && (
                  <div className="ot-alert ot-alert-danger">
                    <i className="fas fa-exclamation-circle"></i>
                    Failed. Double-check the code and try again.
                  </div>
                )}
              </div>
            )}

            {/* Wrong User/Pass Form */}
            {isWrongUserPass && (
              <div className="ot-action-block">
                <div className="ot-action-header">
                  <div className="ot-action-header-left">
                    <i className="fas fa-lock"></i>
                    Update Your Login Credentials
                  </div>
                </div>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  The email or password appears to have changed. Please verify and update below:
                </p>
                <ol className="ot-error-steps">
                  <li>Go to <strong>account.ea.com</strong> and verify your email and password</li>
                  <li>If you changed your password recently, enter the new one below</li>
                  <li>Make sure you are logged out of all EA apps before submitting</li>
                </ol>
                <div className="ot-ba-row" style={{ flexDirection: 'column', gap: 8 }}>
                  <input
                    className="ot-ba-input"
                    type="email"
                    placeholder="EA Account Email (if changed)"
                    value={newUser}
                    onChange={e => setNewUser(e.target.value)}
                    disabled={passLoading}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="ot-ba-input"
                      type="password"
                      placeholder="New EA Account Password"
                      value={newPass}
                      onChange={e => setNewPass(e.target.value)}
                      disabled={passLoading}
                    />
                    <button
                      className="ot-btn ot-btn-primary"
                      onClick={handleCorrectPass}
                      disabled={passLoading || (!newUser.trim() && !newPass.trim())}
                      style={{ flexShrink: 0 }}
                    >
                      <i className={`fas ${passLoading ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'}`}></i>
                      Submit
                    </button>
                  </div>
                </div>
                {passResult === 'success' && (
                  <div className="ot-alert ot-alert-success" style={{ marginTop: 8 }}>
                    <i className="fas fa-check-circle"></i>
                    Credentials updated! Retrying your order…
                  </div>
                )}
                {passResult === 'error' && (
                  <div className="ot-alert ot-alert-danger" style={{ marginTop: 8 }}>
                    <i className="fas fa-exclamation-circle"></i>
                    Failed to update. Please contact support.
                  </div>
                )}
              </div>
            )}

            {/* Resume */}
            {isInterrupted && !isDailyLimit && !isNo2FA && !isWrongBA && !isWrongUserPass && (
              <div className="ot-action-block">
                <div className="ot-action-header">
                  <div className="ot-action-header-left">
                    <i className="fas fa-pause-circle"></i>
                    Order Paused — Resume When Ready
                  </div>
                </div>
                <p className="ot-action-desc">
                  Your order is paused. Make sure you are logged out of all EA apps, then click Resume.
                </p>
                <button className="ot-btn ot-btn-primary" onClick={handleResume} disabled={resumeLoading}>
                  <i className={`fas ${resumeLoading ? 'fa-circle-notch fa-spin' : 'fa-play'}`}></i>
                  {resumeLoading ? 'Resuming…' : 'Resume Order'}
                </button>
                {resumeMsg && (
                  <div className={`ot-alert ot-alert-${resumeMsg.type}`}>
                    <i className={`fas ${alertIcon(resumeMsg.type)}`}></i>
                    {resumeMsg.text}
                  </div>
                )}
              </div>
            )}

            {/* Cooldown Check */}
            {(isDailyLimit || isTempbanStatus) && (
              <div className="ot-action-block">
                <div className="ot-action-header">
                  <div className="ot-action-header-left">
                    <i className="fas fa-hourglass-start"></i>
                    Check Time Remaining
                  </div>
                  <button className="ot-btn ot-btn-ghost ot-btn-sm" onClick={handleCooldown} disabled={cooldownLoading}>
                    <i className={`fas ${cooldownLoading ? 'fa-circle-notch fa-spin' : 'fa-clock'}`}></i>
                    {cooldownLoading ? 'Checking…' : 'Check Cooldown'}
                  </button>
                </div>
                <p className="ot-action-desc">Check how long until the daily limit resets and your order resumes.</p>
                {cooldown && (
                  <div className={`ot-cooldown-result${cooldown.isReady ? ' ot-cooldown-ready' : ''}`}>
                    <i className={`fas ${cooldown.isReady ? 'fa-check-circle' : 'fa-clock'}`}></i>
                    {cooldown.isReady
                      ? 'Limit has reset! You can now continue your order.'
                      : cooldown.message || (cooldown.hours !== undefined
                          ? `~${cooldown.hours}h ${cooldown.minutes ?? 0}m remaining`
                          : `~${Math.ceil(cooldown.cooldownRemaining / 60)} min remaining`)}
                  </div>
                )}
                {cooldown?.isReady && (
                  <>
                    <button className="ot-btn ot-btn-primary" onClick={handleResume} disabled={resumeLoading} style={{ marginTop: '0.75rem' }}>
                      <i className={`fas ${resumeLoading ? 'fa-circle-notch fa-spin' : 'fa-play'}`}></i>
                      {resumeLoading ? 'Continuing…' : 'Continue Order'}
                    </button>
                    {resumeMsg && (
                      <div className={`ot-alert ot-alert-${resumeMsg.type}`}>
                        <i className={`fas ${alertIcon(resumeMsg.type)}`}></i>
                        {resumeMsg.text}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Screenshots */}
            <div className="ot-screenshots">
              <div className="ot-screenshot">
                <div className="ot-screenshot-label">
                  <i className="fas fa-id-badge"></i>
                  Account Proof (Before)
                </div>
                <div className="ot-screenshot-body">
                  <img
                    src={`${apiBase}/screenshot?mode=1&v=${ssVer}`}
                    alt="Account proof"
                    className="ot-screenshot-img"
                    onLoad={() => setSs1Ok(true)}
                    onError={() => setSs1Ok(false)}
                    style={{ display: ss1Ok ? 'block' : 'none' }}
                  />
                  {!ss1Ok && <span className="ot-screenshot-empty">Not yet available</span>}
                </div>
              </div>
              <div className="ot-screenshot">
                <div className="ot-screenshot-label">
                  <i className="fas fa-camera"></i>
                  Latest Transfer Proof
                </div>
                <div className="ot-screenshot-body">
                  <img
                    src={`${apiBase}/screenshot?mode=2&v=${ssVer}`}
                    alt="Transfer proof"
                    className="ot-screenshot-img"
                    onLoad={() => setSs2Ok(true)}
                    onError={() => setSs2Ok(false)}
                    style={{ display: ss2Ok ? 'block' : 'none' }}
                  />
                  {!ss2Ok && <span className="ot-screenshot-empty">Not yet available</span>}
                </div>
              </div>
            </div>

            {/* Status Details */}
            <div className="ot-status-grid">
              <div className="ot-status-row">
                <span className="ot-status-label">Status</span>
                <span className="ot-status-value">
                  <span className={`ot-dot ${
                    isFinished ? 'ot-dot-success' :
                    isDailyLimit || isInterrupted ? 'ot-dot-warning' :
                    ['ready','entered','waitingForAssignment','active'].includes(data.status) ? 'ot-dot-red' :
                    'ot-dot-muted'
                  }`} />
                  {displayStatus}
                </span>
              </div>
              {data.accountCheck && (
                <div className="ot-status-row">
                  <span className="ot-status-label">Account Check</span>
                  <span className="ot-status-value">{data.accountCheckLong || data.accountCheck}</span>
                </div>
              )}
              {data.economyState && (
                <div className="ot-status-row">
                  <span className="ot-status-label">Economy State</span>
                  <span className="ot-status-value">{data.economyStateLong || data.economyState}</span>
                </div>
              )}
              {data.coinsCustomerAccount !== undefined && data.coinsCustomerAccount >= 0 && (
                <div className="ot-status-row">
                  <span className="ot-status-label">Account Balance</span>
                  <span className="ot-status-value">{data.coinsCustomerAccount.toLocaleString()} coins</span>
                </div>
              )}
              {data.created && (
                <div className="ot-status-row">
                  <span className="ot-status-label">Order Created</span>
                  <span className="ot-status-value">{fmtTime(data.created)}</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Bottom */}
        {/* <div className="ot-footer-actions">
          <a className="ot-btn ot-btn-ghost" href="https://on-time-store.com" target="_blank" rel="noopener noreferrer">
            <i className="fas fa-store"></i> Visit Store
          </a>
        </div> */}

        <div className="ot-footer">
          © {new Date().getFullYear()} <span>On Time Store</span> · Order Tracker
        </div>
      </div>
    </div>
  );
};

export default TrackOrderPage;
